import { createClient } from '@supabase/supabase-js';
import { ENV } from '@/config/env';

const defaultSupabaseUrl = 'https://afvnwwsqnkbxradvcyln.supabase.co';
const defaultSupabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFmdm53d3NxbmtieHJhZHZjeWxuIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTAzNzMzNjIsImV4cCI6MjA2NTk0OTM2Mn0.JwpS8eL3Jkt9pKpxghx8l2hZjZOz6rKeRn9EXHRwQ3A';

const sanitize = (value: unknown, fallback: string): string => {
  if (typeof value !== 'string') {
    return fallback;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return fallback;
  }

  // Remove any embedded whitespace characters (including newlines) that break auth headers
  const cleaned = trimmed.replace(/\s+/g, '');
  return cleaned || fallback;
};

// Use ENV wrapper instead of import.meta.env for Capacitor compatibility
const rawSupabaseUrl = ENV.SUPABASE_URL || defaultSupabaseUrl;
const rawSupabaseAnonKey = ENV.SUPABASE_ANON_KEY || defaultSupabaseAnonKey;

const supabaseUrl = sanitize(rawSupabaseUrl, defaultSupabaseUrl);
const supabaseAnonKey = sanitize(rawSupabaseAnonKey, defaultSupabaseAnonKey);

export const supabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
  realtime: {
    params: {
      apikey: supabaseAnonKey,
    },
  },
});

const applyRealtimeAuth = (token?: string) => {
  try {
    supabaseClient.realtime.setAuth(token || supabaseAnonKey);
  } catch (error) {
    console.warn('[SupabaseClient] Failed to apply realtime auth token:', error);
  }
};

// Prime realtime with anon key so public channels work before auth resolves
applyRealtimeAuth();

// If a session is already cached locally, upgrade the realtime token immediately
supabaseClient.auth
  .getSession()
  .then(({ data }) => {
    if (data?.session?.access_token) {
      applyRealtimeAuth(data.session.access_token);
    }
  })
  .catch((error) => {
    console.warn('[SupabaseClient] Failed to hydrate realtime auth from cached session:', error);
  });

// Keep realtime socket auth in sync with Supabase session
supabaseClient.auth.onAuthStateChange((_event, session) => {
  applyRealtimeAuth(session?.access_token);
});
